import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchInput from 'components/AGForm/Inputs/SearchInput/SearchInput';
import BarsIcon from 'components/Icons/BarsIcon';
import Modal, { ModalRef } from 'components/Modal/Modal';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import CameraImageCard from './CameraImageCard';
import { CAMERA_IMAGES_EMPTY_MESSAGE, toGridCardImage } from './cameraViewHelpers';
import useAllCameraImages from './useAllCameraImages';
import styles from './CameraViewAllModal.module.css';

const DEFAULT_PAGE_SIZE = 24;
const INITIAL_LOADING_CARD_COUNT = 9;
const LOAD_MORE_CARD_COUNT = 3;

type CameraViewAllModalProps = {
    spotUuid: string;
    onClose: () => void;
    isDemoMode?: boolean;
    showSearchAndFilterControls?: boolean;
    pageSize?: number;
};

export default function CameraViewAllModal({
    spotUuid,
    onClose,
    isDemoMode = false,
    showSearchAndFilterControls = false,
    pageSize = DEFAULT_PAGE_SIZE,
}: CameraViewAllModalProps): JSX.Element {
    const modalRef = useRef<ModalRef>(null);
    const [plateQuery, setPlateQuery] = useState('');
    const [vehicleQuery, setVehicleQuery] = useState('');

    const { images, hasMore, isInitialLoading, isLoadingMore, errorMessage, loadMore, reload } = useAllCameraImages({
        spotUuid,
        isOpen: true,
        pageSize,
        isDemoMode,
    });

    const target = useInfiniteScroll({ loadMore });

    useEffect(() => {
        modalRef.current?.openModal();
    }, []);

    const gridImages = useMemo(() => images.map(toGridCardImage), [images]);
    const hasImages = gridImages.length > 0;
    const shouldRenderEmptyState = !isInitialLoading && !errorMessage && !hasImages;
    const shouldRenderGrid = !isInitialLoading && hasImages;
    const shouldRenderInitialError = !isInitialLoading && !!errorMessage && !hasImages;

    return (
        <Modal ref={modalRef} title="Cameras" onClose={onClose}>
            <div className={styles.body} data-testid="camera-view-all-modal">
                {showSearchAndFilterControls ? (
                    <div className={styles.controlsRow} data-testid="camera-view-all-controls">
                        <SearchInput
                            placeholder="License Plate"
                            value={plateQuery}
                            onChange={(event) => setPlateQuery(event.target.value)}
                            aria-label="License Plate"
                            className={styles.controlInput}
                        />
                        <SearchInput
                            placeholder="Search by color, make, model, or description"
                            value={vehicleQuery}
                            onChange={(event) => setVehicleQuery(event.target.value)}
                            aria-label="Search by color, make, model, or description"
                            className={`${styles.controlInput} ${styles.wideInput}`}
                        />
                        <button
                            type="button"
                            className={styles.filtersButton}
                            aria-label="Filters"
                            onClick={() => undefined}
                            data-testid="camera-view-all-filters-button"
                        >
                            <BarsIcon className={styles.filtersIcon} />
                            Filters
                        </button>
                    </div>
                ) : null}

                <div className={styles.contentSection}>
                    {isInitialLoading ? (
                        <div className={styles.grid} data-testid="camera-view-all-initial-skeleton">
                            {Array.from({ length: INITIAL_LOADING_CARD_COUNT }).map((_, index) => (
                                <div key={`camera-view-all-skeleton-${index}`} className={styles.skeletonCard}>
                                    <RectangleSkeleton />
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {shouldRenderInitialError ? <WidgetErrorMessage message={errorMessage} onRetry={reload} /> : null}
                    {shouldRenderEmptyState ? <p className={styles.emptyState}>{CAMERA_IMAGES_EMPTY_MESSAGE}</p> : null}

                    {shouldRenderGrid ? (
                        <div className={styles.grid} data-testid="camera-view-all-grid">
                            {gridImages.map((image, index) => (
                                <div key={`${image.src}-${index}`} className={styles.imageCard}>
                                    <CameraImageCard image={image} isExpandable />
                                </div>
                            ))}
                            {isLoadingMore
                                ? Array.from({ length: LOAD_MORE_CARD_COUNT }).map((_, index) => (
                                      <div
                                          key={`camera-view-all-loading-more-${index}`}
                                          className={styles.skeletonCard}
                                      >
                                          <RectangleSkeleton />
                                      </div>
                                  ))
                                : null}
                        </div>
                    ) : null}
                    {errorMessage && hasImages ? <p className={styles.loadMoreError}>{errorMessage}</p> : null}

                    {hasMore ? (
                        <span ref={target} className={styles.scrollTarget} data-testid="camera-view-all-target" />
                    ) : null}
                </div>
            </div>
        </Modal>
    );
}
